import { useState, useEffect } from 'react';
import { Map, AdvancedMarker, useMapsLibrary, useMap, GoogleMapsContext} from '@vis.gl/react-google-maps'
import MapKey from './key';
import { ICombinedLocations } from '../../places/type';
import { combinedLocations } from '../../places';
import {CrcMarker, CreMarker, MixedMarker, LocationMarker} from './markers';
import {config} from '../../config'
import LocationProps from '../../props/LocationProps';
import SearchSetProps from '../../props/SearchSetProps';
import LocationSort from '../../helpers/sort';
import './map.scss'

type MapProps = {
  mapId: string,
  userLatLng: google.maps.LatLngLiteral | null
  userSetByGeo: boolean
  userSetBySearch: boolean
  userBounds: google.maps.LatLngBounds | null | undefined
  setUserShowModal: (flag: boolean) => void
  setActiveLocation: (loc: LocationProps[]) => void
  locations: LocationProps[]
  userSetType: SearchSetProps
  setInitialBounds: (place: google.maps.LatLngBounds | undefined) => void;
  setModalTrigger: (el: HTMLElement | null) => void
};

type PlaceMarkersProps = {
  pinLocations: ICombinedLocations,
  setUserShowModal: (flag: boolean) => void
  setActiveLocation: (loc: LocationProps[]) => void
  setModalTrigger: (el: HTMLElement | null) => void

}

const PlaceMarkers = ({
  pinLocations,
  setUserShowModal,
  setModalTrigger,
  setActiveLocation,
}:PlaceMarkersProps) => {
  return (
    <>
      {pinLocations && Object.values(pinLocations).map(location => {
        let showMarker:boolean = true

        location.locations.forEach(() => {

        // check for all our active items type to see what sort of pin we need to render
        if (location.locations.length > 1) {
          const activeLocations = location.locations.filter(r => r.active)
          if (!activeLocations) return
          const type:string = activeLocations[0]?.type || 'mixed'
          location.type = activeLocations.every(r => r.type == type) ? type : 'mixed'
        }

      })
        showMarker = location.locations.some(r => r.active) || false

        return (
          <>
            {showMarker &&
              <AdvancedMarker
                key={location.locations[0].id}
                position={{
                  lat: Number(location.locations[0].location.lat),
                  lng: Number(location.locations[0].location.lng)
                }}
                clickable={true}
                onClick={(evt) => {
                  evt.stop()
                  setActiveLocation([...location.locations])
                  setModalTrigger(document.activeElement as HTMLElement)
                  setUserShowModal(true)
                }}>
                  {location.type === 'crc' && <CrcMarker size='medium' />}
                  {location.type === 'cre' && <CreMarker size='medium' />}
                  {location.type === 'mixed' && <MixedMarker size='medium' />}
              </AdvancedMarker>
            }
          </>
        )
      }
    )
  }
  </>
  )
}

const ShowMap = ({
  mapId,
  userLatLng,
  userBounds,
  setUserShowModal,
  setActiveLocation,
  setModalTrigger,
  locations,
  userSetType,
  setInitialBounds
}:MapProps) => {

  const map = useMap();
  const defaultZoom:number = config.map.zoom
  const [pinLocations, setPinLocations] = useState({})
  const [mapInited, setMapInited] = useState<boolean>(false)
  const [autoBoundsSuccess, setAutoBoundsSuccess] = useState<boolean>(false)
  const [localDistance, setLocalDistance] = useState<number>(Infinity)
  const [closestLocation, setClosestLocation] = useState<LocationProps | undefined>(undefined)
  // const geo = useMapsLibrary('geometry')

  const findClosest = (closest:boolean = true) => {
    if (!userLatLng) return

    const sortedLocations:LocationProps[] = LocationSort.distance(locations)

    if (!closest) {
      sortedLocations.reverse()
    }
    const filteredLocations = sortedLocations.filter(f => f.active)
    const closestLocation = filteredLocations[0]
    if (!closestLocation) return
    const filteredDistance = closestLocation.distance

    if (filteredDistance && localDistance !== filteredDistance) {
      setLocalDistance(() => filteredDistance)
      setClosestLocation(() => closestLocation)
    }
  }

  const autoBound = (bounds:google.maps.LatLngBounds | undefined) => {
    if (!bounds || !map) return

    const hasPins = locations.some(l => l.active && bounds.contains(new google.maps.LatLng(l.location.lat, l.location.lng)))
    if (!hasPins) {
      // there are no pins in view.
      const z = map.getZoom()
      if (!z || z <= config.map.zoom) return
      map.setZoom(z - .75) // leveraging fractional zoom to make the jump not quite so big, but big enough to show an entire pin
      autoBound(map.getBounds())
    } else if (closestLocation?.distance &&localDistance !== closestLocation?.distance) {
      // Found pins, zoom out one more level for better context
      const currentZoom = map.getZoom()
      if (currentZoom) {
        map.setZoom(currentZoom - .75)
      }
      setAutoBoundsSuccess(true)
    }
  }

  useEffect(() => {
    if (!userLatLng?.lat || !map) return

    setAutoBoundsSuccess(false)

    map?.setCenter(userLatLng)
    const b = new google.maps.LatLngBounds()
    b.extend(userLatLng)
    google.maps.event.addListenerOnce(map, 'idle', function() {
      map?.fitBounds(b)
      autoBound(b)

    });

  }, [userLatLng, map])

  useEffect(() => {
    if (locations?.length && map) {
      const combinedPins = combinedLocations(locations)
      setPinLocations(combinedPins)
      autoBound(map.getBounds())
    }
  }, [locations, map])

  const setSearchBounds = () => {
    if (mapInited) return
    setMapInited(true)
    setInitialBounds(new google.maps.LatLngBounds(new google.maps.LatLng(config.search.bounds.sw.lat, config.search.bounds.sw.lng), new google.maps.LatLng(config.search.bounds.ne.lat, config.search.bounds.ne.lng)))
  }

  return (
    <>
      <div className='crc-map nsw-m-top-sm'>
        <Map
        scaleControl={true}
        fullscreenControl={false}
        defaultZoom={defaultZoom}
        minZoom={defaultZoom}
        mapId={mapId}
        defaultCenter={ config.map.center }
        reuseMaps={true}
        onTilesLoaded={setSearchBounds}
        isFractionalZoomEnabled={true}
        >
          <PlaceMarkers setActiveLocation={setActiveLocation} setUserShowModal={setUserShowModal} pinLocations={pinLocations} setModalTrigger={setModalTrigger}/>
          {(userSetType.type &&['address', 'browser'].includes(userSetType.type)) && <AdvancedMarker position={userLatLng} ><LocationMarker size='medium' /></AdvancedMarker>}
        </Map>
      </div>
      <MapKey />
    </>
  )
}

export default ShowMap
